import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bot, Plus, Trash2, Save, Play, KeyRound, History, RefreshCw, Beaker, ShieldCheck, ChevronRight } from "lucide-react";

const PROVIDER_TYPES = [
  { value: "gemini", label: "Google Gemini" },
  { value: "openai", label: "OpenAI" },
  { value: "anthropic", label: "Anthropic Claude" },
  { value: "openrouter", label: "OpenRouter" },
  { value: "openai_compatible", label: "OpenAI-Compatible (self-hosted)" },
  { value: "groq", label: "Groq" },
  { value: "llm7", label: "LLM7" },
];

const STATUS_STYLES = {
  pending: "bg-[#F3EFEA] text-[#5C6661]",
  processing: "bg-[#D4A373]/20 text-[#8a5a2a]",
  completed: "bg-[#2C4C3B]/10 text-[#2C4C3B]",
  failed: "bg-red-100 text-red-700",
};

function ProvidersPanel({ isSuper }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [testing, setTesting] = useState(null);
  const [form, setForm] = useState({ display_name: "", provider_type: "gemini", model: "gemini-3-flash-preview", enabled: true, is_default: false, base_url: "", temperature: 0.7, max_output_tokens: 4096, api_key: "" });

  const load = async () => setItems((await api.get("/ai/providers")).data || []);
  useEffect(() => { load(); }, []);

  const reset = () => { setForm({ display_name: "", provider_type: "gemini", model: "gemini-3-flash-preview", enabled: true, is_default: false, base_url: "", temperature: 0.7, max_output_tokens: 4096, api_key: "" }); setEditing(null); setShowForm(false); };

  const save = async () => {
    if (!form.display_name.trim() || !form.model.trim()) { toast.error("Display name and model are required"); return; }
    try {
      if (editing) {
        const patch = { ...form };
        if (!patch.api_key) delete patch.api_key; // don't overwrite unless changed
        await api.put(`/ai/providers/${editing}`, patch);
        toast.success("Provider updated");
      } else {
        if (!form.api_key) { toast.error("API key required for new provider"); return; }
        await api.post("/ai/providers", form);
        toast.success("Provider created");
      }
      reset(); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const edit = (p) => {
    setEditing(p.id);
    setForm({ display_name: p.display_name, provider_type: p.provider_type, model: p.model, enabled: !!p.enabled, is_default: !!p.is_default, base_url: p.base_url || "", temperature: p.temperature ?? 0.7, max_output_tokens: p.max_output_tokens ?? 2048, api_key: "" });
    setShowForm(true);
  };

  const del = async (id) => { if (!window.confirm("Delete this provider?")) return; try { await api.delete(`/ai/providers/${id}`); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };

  const test = async (id) => {
    setTesting(id);
    try {
      const { data } = await api.post(`/ai/providers/${id}/test`);
      if (data.ok) toast.success(`Test OK — ${(data.sample || "").slice(0, 80)}`);
      else toast.error(`Test failed: ${data.reason || "unknown"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setTesting(null); }
  };

  return (
    <div className="space-y-6" data-testid="ai-providers-panel">
      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h3 className="font-serif-display text-2xl text-[#1C201E] flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#2C4C3B]" /> AI Providers</h3>
            <p className="text-xs text-[#5C6661] mt-1">API keys are encrypted at rest with Fernet. Only the last 4 chars of the fingerprint are ever displayed.</p>
          </div>
          {isSuper && (
            <button data-testid="ai-provider-new" onClick={() => { reset(); setShowForm(true); }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326]"><Plus className="w-4 h-4" /> Add provider</button>
          )}
        </div>

        {showForm && isSuper && (
          <div className="mb-6 p-5 border border-[#D4A373] rounded-xl bg-[#F3EFEA]/40" data-testid="ai-provider-form">
            <div className="grid md:grid-cols-2 gap-3">
              <input data-testid="ai-provider-name" placeholder="Display name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
              <select data-testid="ai-provider-type" value={form.provider_type} onChange={(e) => setForm({ ...form, provider_type: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#D4A373]">
                {PROVIDER_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
              <input data-testid="ai-provider-model" placeholder="Model (e.g. gemini-2.0-flash-exp)" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
              <input data-testid="ai-provider-baseurl" placeholder="Base URL (optional)" value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
              <input data-testid="ai-provider-temp" type="number" step="0.1" min="0" max="2" placeholder="Temperature" value={form.temperature} onChange={(e) => setForm({ ...form, temperature: parseFloat(e.target.value || 0) })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
              <input data-testid="ai-provider-maxtok" type="number" min="16" max="16384" placeholder="Max output tokens" value={form.max_output_tokens} onChange={(e) => setForm({ ...form, max_output_tokens: parseInt(e.target.value || 0, 10) })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
              <input data-testid="ai-provider-apikey" type="password" placeholder={editing ? "Leave blank to keep existing key" : "API key"} value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} className="md:col-span-2 px-4 py-2 rounded-lg border border-[#E5E0D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
            </div>
            <div className="mt-3 flex flex-wrap gap-4 items-center text-sm text-[#1C201E]">
              <label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} /> Enabled</label>
              <label className="inline-flex items-center gap-2"><input data-testid="ai-provider-default" type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} /> Set as default</label>
            </div>
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={reset} className="px-4 py-2 rounded-full border border-[#E5E0D8] text-sm">Cancel</button>
              <button data-testid="ai-provider-save" onClick={save} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326]"><Save className="w-4 h-4" /> Save</button>
            </div>
          </div>
        )}

        <div className="divide-y divide-[#E5E0D8]">
          {items.map((p, i) => (
            <div key={p.id} data-testid={`ai-provider-row-${i}`} className="py-4 flex items-center justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-[#1C201E]">{p.display_name}</span>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#F3EFEA] text-[#5C6661]">{p.provider_type}</span>
                  {p.is_default && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#2C4C3B] text-white">default</span>}
                  {!p.enabled && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-100 text-red-700">disabled</span>}
                </div>
                <div className="text-xs text-[#5C6661] mt-1 truncate">Model: <code>{p.model}</code> · Key: {p.api_key_configured ? <code>{p.api_key_masked}</code> : <span className="text-red-600">no credential</span>}</div>
              </div>
              <div className="flex gap-2">
                <button data-testid={`ai-provider-test-${i}`} onClick={() => test(p.id)} disabled={testing === p.id} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#2C4C3B] text-[#2C4C3B] text-xs hover:bg-[#2C4C3B] hover:text-white disabled:opacity-60"><Beaker className="w-3 h-3" /> {testing === p.id ? "Testing..." : "Test"}</button>
                {isSuper && <button data-testid={`ai-provider-edit-${i}`} onClick={() => edit(p)} className="px-3 py-1.5 rounded-full border border-[#E5E0D8] text-[#1C201E] text-xs hover:bg-[#F3EFEA]">Edit</button>}
                {isSuper && <button data-testid={`ai-provider-del-${i}`} onClick={() => del(p.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>}
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="text-sm text-[#5C6661] py-2">No providers yet — add Gemini/OpenAI/Anthropic/OpenRouter to enable generation.</div>}
        </div>
      </div>
    </div>
  );
}

function PromptsPanel() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState("");
  const [versions, setVersions] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [previewIn, setPreviewIn] = useState({ date: new Date().toISOString().slice(0, 10), bible_version: "KJV", start_date: "", end_date: "", content_type: "devotion", timezone: "Asia/Manila", church_name: "Bible Baptist Church Barani", existing_dates: [], number_of_days: 7 });
  const [preview, setPreview] = useState(null);

  const load = async () => {
    const { data } = await api.get("/ai/prompts");
    setItems(data || []);
    if (data?.length && !selected) setSelected(data[0]);
  };
  useEffect(() => { load(); api.get("/ai/prompts/placeholders").then((r) => setPlaceholders(r.data.allowed || [])); }, []); // eslint-disable-line

  useEffect(() => {
    if (selected) { setContent(selected.content || ""); api.get(`/ai/prompts/${selected.id}/versions`).then((r) => setVersions(r.data || [])); }
  }, [selected?.id]); // eslint-disable-line

  const save = async () => {
    if (!selected) return;
    try {
      await api.put(`/ai/prompts/${selected.id}`, { content });
      toast.success("Prompt saved (new version)");
      await load(); const { data } = await api.get(`/ai/prompts/${selected.id}/versions`); setVersions(data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const restore = async (v) => {
    if (!window.confirm(`Restore version ${v}?`)) return;
    try { await api.post(`/ai/prompts/${selected.id}/restore/${v}`); toast.success(`Restored v${v}`); await load(); const { data } = await api.get(`/ai/prompts/${selected.id}/versions`); setVersions(data || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const doPreview = async () => {
    if (!selected) return;
    try { const { data } = await api.post(`/ai/prompts/${selected.id}/preview`, previewIn); setPreview(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="ai-prompts-panel">
      <div className="lg:col-span-3">
        <div className="p-3 bg-white border border-[#E5E0D8] rounded-xl">
          <div className="text-xs uppercase tracking-widest text-[#5C6661] px-3 pt-2 pb-1">Prompts</div>
          {items.map((p, i) => (
            <button
              key={p.id}
              data-testid={`ai-prompt-select-${p.prompt_type}`}
              onClick={() => setSelected(p)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${selected?.id === p.id ? "bg-[#2C4C3B] text-white" : "hover:bg-[#F3EFEA] text-[#1C201E]"}`}
            >
              <div className="font-medium">{p.name}</div>
              <div className={`text-xs mt-0.5 ${selected?.id === p.id ? "text-white/70" : "text-[#5C6661]"}`}>{p.prompt_type} · v{p.version}</div>
            </button>
          ))}
          {items.length === 0 && <div className="p-3 text-xs text-[#5C6661]">No prompts loaded.</div>}
        </div>
      </div>
      <div className="lg:col-span-9 space-y-4">
        {selected && (
          <>
            <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#5C6661]">{selected.prompt_type} · v{selected.version}</div>
                  <h3 className="font-serif-display text-2xl text-[#1C201E]">{selected.name}</h3>
                  <p className="text-xs text-[#5C6661] mt-1">{selected.description}</p>
                </div>
                <button data-testid="ai-prompt-save" onClick={save} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326]"><Save className="w-4 h-4" /> Save new version</button>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5 text-[10px]">
                <span className="uppercase tracking-widest text-[#5C6661] pr-1">placeholders:</span>
                {placeholders.map((ph) => <code key={ph} className="px-2 py-0.5 bg-[#F3EFEA] text-[#8a5a2a] rounded">{`{{${ph}}}`}</code>)}
              </div>
              <textarea data-testid="ai-prompt-content" value={content} onChange={(e) => setContent(e.target.value)} rows={14} className="mt-4 w-full px-4 py-3 rounded-lg border border-[#E5E0D8] font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-white border border-[#E5E0D8] rounded-xl">
                <div className="font-medium text-[#1C201E] flex items-center gap-2 mb-3"><History className="w-4 h-4 text-[#8a5a2a]" /> Version history ({versions.length})</div>
                <div className="max-h-64 overflow-auto divide-y divide-[#E5E0D8]">
                  {versions.map((v, i) => (
                    <div key={i} className="py-2 flex items-center justify-between gap-2">
                      <div className="text-xs text-[#5C6661]">v{v.version}{v.restore_of ? ` (restore of v${v.restore_of})` : ""} · {new Date(v.changed_at).toLocaleString()} · {v.changed_by}</div>
                      <button data-testid={`ai-prompt-restore-${i}`} onClick={() => restore(v.version)} className="px-2.5 py-1 rounded-full border border-[#2C4C3B] text-[#2C4C3B] text-xs hover:bg-[#2C4C3B] hover:text-white">Restore</button>
                    </div>
                  ))}
                  {versions.length === 0 && <div className="text-xs text-[#5C6661] py-2">No previous versions yet.</div>}
                </div>
              </div>

              <div className="p-4 bg-white border border-[#E5E0D8] rounded-xl">
                <div className="font-medium text-[#1C201E] flex items-center gap-2 mb-3"><RefreshCw className="w-4 h-4 text-[#2C4C3B]" /> Live preview</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <input placeholder="date" value={previewIn.date} onChange={(e) => setPreviewIn({ ...previewIn, date: e.target.value })} className="px-3 py-2 rounded border border-[#E5E0D8]" />
                  <input placeholder="bible_version" value={previewIn.bible_version} onChange={(e) => setPreviewIn({ ...previewIn, bible_version: e.target.value })} className="px-3 py-2 rounded border border-[#E5E0D8]" />
                  <input placeholder="start_date" value={previewIn.start_date} onChange={(e) => setPreviewIn({ ...previewIn, start_date: e.target.value })} className="px-3 py-2 rounded border border-[#E5E0D8]" />
                  <input placeholder="end_date" value={previewIn.end_date} onChange={(e) => setPreviewIn({ ...previewIn, end_date: e.target.value })} className="px-3 py-2 rounded border border-[#E5E0D8]" />
                </div>
                <button data-testid="ai-prompt-preview-btn" onClick={doPreview} className="mt-3 px-4 py-1.5 rounded-full border border-[#2C4C3B] text-[#2C4C3B] text-xs hover:bg-[#2C4C3B] hover:text-white">Render preview</button>
                {preview && (
                  <div className="mt-3 space-y-2 text-xs">
                    <div><b className="text-[#5C6661]">SYSTEM:</b><pre className="mt-1 whitespace-pre-wrap p-2 bg-[#F3EFEA] rounded text-[#1C201E]">{preview.system}</pre></div>
                    <div><b className="text-[#5C6661]">USER:</b><pre className="mt-1 whitespace-pre-wrap p-2 bg-[#F3EFEA] rounded text-[#1C201E]">{preview.user}</pre></div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function JobsPanel() {
  const [providers, setProviders] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [form, setForm] = useState({ content_type: "devotion", provider_id: "", start_date: new Date().toISOString().slice(0, 10), end_date: new Date(Date.now() + 6 * 86400000).toISOString().slice(0, 10), bible_version: "KJV", timezone_name: "Asia/Manila" });
  const [running, setRunning] = useState(false);

  const loadAll = async () => {
    setProviders(((await api.get("/ai/providers")).data || []).filter((p) => p.enabled && p.api_key_configured));
    setJobs((await api.get("/ai/jobs")).data || []);
  };
  useEffect(() => { loadAll(); }, []);

  const run = async () => {
    setRunning(true);
    try {
      const payload = { ...form };
      if (!payload.provider_id) delete payload.provider_id;
      const { data } = await api.post("/ai/generate", payload);
      toast.success(`Job ${data.job_id ? "created" : "already covered"} — saved ${data.saved || 0} item(s)`);
      await loadAll();
    } catch (e) { toast.error(typeof (e?.response?.data?.detail) === "string" ? e.response.data.detail : "Generation failed"); }
    finally { setRunning(false); }
  };

  return (
    <div className="space-y-6" data-testid="ai-jobs-panel">
      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
        <h3 className="font-serif-display text-2xl text-[#1C201E] flex items-center gap-2"><Play className="w-5 h-5 text-[#2C4C3B]" /> Generate content</h3>
        <p className="text-xs text-[#5C6661] mt-1">AI-generated verses & devotions are saved as <b>Review</b> — never auto-published. Existing dates are skipped. Range ≤ 31 days.</p>
        <div className="mt-4 grid md:grid-cols-3 gap-3">
          <select data-testid="ai-gen-type" value={form.content_type} onChange={(e) => setForm({ ...form, content_type: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]">
            <option value="devotion">Daily Devotion</option>
            <option value="verse">Daily Verse</option>
          </select>
          <select data-testid="ai-gen-provider" value={form.provider_id} onChange={(e) => setForm({ ...form, provider_id: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]">
            <option value="">Default provider</option>
            {providers.map((p) => <option key={p.id} value={p.id}>{p.display_name} ({p.provider_type})</option>)}
          </select>
          <input data-testid="ai-gen-version" placeholder="Bible version" value={form.bible_version} onChange={(e) => setForm({ ...form, bible_version: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
          <input data-testid="ai-gen-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
          <input data-testid="ai-gen-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
          <button data-testid="ai-gen-run" onClick={run} disabled={running || providers.length === 0} className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326] disabled:opacity-60">
            {running ? "Generating..." : <><Bot className="w-4 h-4" /> Generate</>}
          </button>
        </div>
        {providers.length === 0 && <div className="mt-3 text-xs text-red-700">Add and enable at least one provider with an API key to run generations.</div>}
      </div>

      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif-display text-2xl text-[#1C201E]">Generation history ({jobs.length})</h3>
          <button onClick={loadAll} className="p-2 rounded-full border border-[#E5E0D8] text-[#5C6661] hover:bg-[#F3EFEA]"><RefreshCw className="w-4 h-4" /></button>
        </div>
        <div className="divide-y divide-[#E5E0D8]">
          {jobs.map((j, i) => (
            <div key={j.id} data-testid={`ai-job-row-${i}`} className="py-4 flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${STATUS_STYLES[j.status] || STATUS_STYLES.pending}`}>{j.status}</span>
                  <span className="font-medium text-[#1C201E]">{j.content_type} · {j.start_date} → {j.end_date}</span>
                </div>
                <div className="text-xs text-[#5C6661] mt-1">{j.provider} · {j.model} · saved {j.saved ?? 0} · by {j.requested_by} · {new Date(j.created_at).toLocaleString()}</div>
                {j.error && <div className="text-xs text-red-600 mt-1">Error: {typeof j.error === "string" ? j.error : JSON.stringify(j.error)}</div>}
                {j.note && <div className="text-xs text-[#5C6661] italic mt-1">{j.note}</div>}
              </div>
              <div className="text-xs text-[#5C6661]"><ChevronRight className="w-4 h-4 inline" /> {j.completed_batches || 0}/{j.total_batches || 1}</div>
            </div>
          ))}
          {jobs.length === 0 && <div className="text-sm text-[#5C6661]">No jobs yet.</div>}
        </div>
      </div>
    </div>
  );
}

function AuditPanel() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/ai/audit").then((r) => setItems(r.data || [])).catch(() => {}); }, []);
  return (
    <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl" data-testid="ai-audit-panel">
      <h3 className="font-serif-display text-2xl text-[#1C201E] flex items-center gap-2 mb-4"><ShieldCheck className="w-5 h-5 text-[#2C4C3B]" /> Audit log ({items.length})</h3>
      <div className="divide-y divide-[#E5E0D8] max-h-[520px] overflow-auto">
        {items.map((a, i) => (
          <div key={i} className="py-2.5 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm text-[#1C201E]"><b>{a.action}</b> · {a.actor}</div>
              {a.meta && Object.keys(a.meta).length > 0 && <div className="text-xs text-[#5C6661] mt-0.5 truncate">{JSON.stringify(a.meta)}</div>}
            </div>
            <div className="text-xs text-[#5C6661] whitespace-nowrap">{new Date(a.at).toLocaleString()}</div>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-[#5C6661]">No events yet.</div>}
      </div>
    </div>
  );
}

const SUBTABS = [
  { key: "providers", label: "Providers" },
  { key: "prompts", label: "Prompts" },
  { key: "jobs", label: "Generation Jobs" },
  { key: "audit", label: "Audit Log" },
];

export function AiTab({ role }) {
  const [sub, setSub] = useState("providers");
  const isSuper = role === "super_admin";
  return (
    <div className="space-y-6" data-testid="ai-tab">
      <div className="p-4 bg-white border border-[#E5E0D8] rounded-xl flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#2C4C3B]/10 flex items-center justify-center"><Bot className="w-5 h-5 text-[#2C4C3B]" /></div>
          <div>
            <div className="font-serif-display text-xl text-[#1C201E]">AI Content Manager</div>
            <div className="text-xs text-[#5C6661]">Multi-provider generation for Daily Verse &amp; Devotion. Credentials encrypted at rest. AI drafts must always be reviewed before publishing.</div>
          </div>
        </div>
        <div className="inline-flex bg-[#F3EFEA] border border-[#E5E0D8] rounded-full p-1 flex-wrap">
          {SUBTABS.map((s) => (
            <button
              key={s.key}
              data-testid={`ai-subtab-${s.key}`}
              onClick={() => setSub(s.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium ${sub === s.key ? "bg-[#2C4C3B] text-white" : "text-[#1C201E]"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {sub === "providers" && <ProvidersPanel isSuper={isSuper} />}
      {sub === "prompts" && <PromptsPanel />}
      {sub === "jobs" && <JobsPanel />}
      {sub === "audit" && <AuditPanel />}
    </div>
  );
}

export default AiTab;
